import { AffiliateLinkApiResponse, AffiliatePromotionLink } from '@/@types/AffiliatePromotionLink';
import { AffiliateReportLinkResponse } from '@/@types/GlobalAffiliateReportResponse';
import { formatCurrencyFromCents } from '@/lib/select-options';
import { Currency } from '@/store/useCurrencyStore';

function affiliateLinkSortKey(link: AffiliatePromotionLink): string {
  return [
    link.productName ?? '',
    link.offerName ?? '',
    link.label ?? link.checkoutName ?? '',
  ]
    .map((value) => value.trim().toLowerCase())
    .join('\0');
}

export function sortAffiliatePromotionLinks(
  links: AffiliatePromotionLink[],
): AffiliatePromotionLink[] {
  return [...links].sort((a, b) =>
    affiliateLinkSortKey(a).localeCompare(affiliateLinkSortKey(b), 'pt-BR'),
  );
}

export function mapStringLinksToPromotionLinks(
  links: string[],
): AffiliatePromotionLink[] {
  return sortAffiliatePromotionLinks(
    links.map((url, index) => ({
      id: `link-${index}`,
      url,
    })),
  );
}

export function mapApiLinksToPromotionLinks(
  links: AffiliateLinkApiResponse[] | string[] | undefined,
  currency: Currency = 'BRL',
): AffiliatePromotionLink[] {
  if (!links?.length) return [];

  if (typeof links[0] === 'string') {
    return mapStringLinksToPromotionLinks(links as string[]);
  }

  return sortAffiliatePromotionLinks(
    (links as AffiliateLinkApiResponse[]).map((link) => ({
      id: link.id,
      url: link.url,
      label: link.label,
      checkoutName: link.checkoutName,
      offerName: link.offerName ?? undefined,
      productName: link.productName,
      offerPriceFormatted:
        link.offerPrice != null
          ? formatCurrencyFromCents(link.offerPrice, currency)
          : undefined,
    })),
  );
}

export function mapReportLinksToPromotionLinks(
  links: AffiliateReportLinkResponse[] | undefined,
): AffiliatePromotionLink[] {
  if (!links?.length) return [];

  return sortAffiliatePromotionLinks(
    links.map((link) => ({
      id: link.id,
      url: link.url,
      label: link.label,
      checkoutName: link.checkoutName,
      offerName: link.offerName ?? undefined,
      offerPrice: link.offerPrice ?? undefined,
      productName: link.productName,
    })),
  );
}

export function hasMultipleAffiliateLinkProducts(
  links: AffiliatePromotionLink[],
): boolean {
  const productNames = links
    .map((link) => link.productName?.trim())
    .filter((name): name is string => Boolean(name));

  return new Set(productNames).size > 1;
}

export function getNameInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}
